'use es6';

import I18n from 'I18n';
import { List, Set as ImmutableSet } from 'immutable';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { NavMarker } from 'react-rhumb';
import getIn from 'transmute/getIn';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Paginator as FoundationsPaginator } from 'foundations-components/transitional/components/Paginator';
import { Table } from 'foundations-components/transitional/components/Table';
import { ResultsMessage } from 'foundations-components/transitional/components/ResultsMessage';
import { getId } from 'reporting-action-components/data/schemas/reportSchema';
import { setRestorableReportsFilters } from '../actions/reportsActions';
import RestoreReportsTableHeader from '../components/reportsTable/restore/RestoreReportsTableHeader';
import RestoreReportsTableRow from '../components/reportsTable/restore/RestoreReportsTableRow';
import requestStatusType from '../constants/requestStatusTypes';
import { trackEvent, EVENTS } from '../lib/usageTracker';
import { selectRestoreReportsVisibleColumns } from '../lib/selectRestoreReportsVisibleColumns';
import { useSelectedRows } from '../redux/selectedRows';
import { selectRestoreTableData } from '../selectors/tableData';
import { selectRestorableReportsRequestStatus } from '../selectors/reportsSelector';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const {
  UNINITIALIZED,
  PENDING,
  SUCCEEDED,
  FAILED
} = requestStatusType;
const RestoreReportsTable = props => {
  const {
    buOptions,
    isBusinessUnitsLoading,
    tableData,
    tableDataStatus,
    actions,
    filters,
    reportCount
  } = props;
  const {
    offset,
    limit
  } = filters;
  const actionableRows = ImmutableSet(tableData);
  const {
    selectedRows,
    selectRow,
    deselectRow
  } = useSelectedRows({
    actionableRows
  });
  const visibleColumns = useSelector(selectRestoreReportsVisibleColumns);
  const numColumns = visibleColumns.length;
  const handleSelectedChange = (row, isSelected) => {
    if (isSelected) {
      selectRow(row);
    } else {
      deselectRow(row);
    }
  };
  const handlePageChange = ({
    target: {
      value: pageNumber
    }
  }) => {
    trackEvent(EVENTS.changePage, {
      pageNumber
    });
    actions.setRestorableReportsFilters(Object.assign({}, filters, {
      offset: (pageNumber - 1) * limit
    }));
  };
  const TableBodyFailure = /*#__PURE__*/_jsx(NavMarker, {
    name: "RESTORE_REPORTS_LIST_CONTAINER_FAILURE",
    children: /*#__PURE__*/_jsx("tbody", {
      children: /*#__PURE__*/_jsx("tr", {
        children: /*#__PURE__*/_jsx("td", {
          colSpan: numColumns,
          children: /*#__PURE__*/_jsx(Alert, {
            type: "danger",
            titleText: I18n.text('reports.table.failedToLoad.title'),
            closeable: false,
            children: I18n.text('reports.table.failedToLoad.description')
          })
        })
      })
    })
  });
  const TableBodyLoading = /*#__PURE__*/_jsx("tbody", {
    children: /*#__PURE__*/_jsx("tr", {
      children: /*#__PURE__*/_jsx("td", {
        colSpan: numColumns,
        children: /*#__PURE__*/_jsx(LoadingSpinner, {
          grow: true
        })
      })
    })
  });
  const PageLoading = /*#__PURE__*/_jsx(LoadingSpinner, {
    "data-test-id": "restore-loader",
    grow: true
  });
  const EmptyState = /*#__PURE__*/_jsx(NavMarker, {
    name: "RESTORE_REPORTS_LIST_CONTAINER_EMPTY",
    children: /*#__PURE__*/_jsx(ResultsMessage, {
      illustration: "empty-state-charts",
      illustrationProps: {
        width: 187
      },
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "reports.restore.table.emptyState"
      })
    })
  });
  const TableBodySuccess = /*#__PURE__*/_jsx(NavMarker, {
    name: "RESTORE_REPORTS_LIST_CONTAINER",
    children: /*#__PURE__*/_jsx("tbody", {
      children: tableData.map(report => /*#__PURE__*/_jsx(RestoreReportsTableRow, {
        buOptions: buOptions,
        report: report,
        onSelectedChange: handleSelectedChange,
        selectedRows: selectedRows
      }, getId(report)))
    })
  });
  const TableBody = tableDataStatus === FAILED ? TableBodyFailure : tableDataStatus !== SUCCEEDED || isBusinessUnitsLoading ? TableBodyLoading : TableBodySuccess;
  const TableColGroup = /*#__PURE__*/_jsx("colgroup", {
    children: visibleColumns.map(columnData => /*#__PURE__*/_jsx("col", {
      style: {
        width: columnData.width
      }
    }, `${columnData.key}-col`))
  });
  const pageCount = Math.ceil(reportCount / limit);
  const Paginator = pageCount > 1 ? /*#__PURE__*/_jsx(FoundationsPaginator, {
    onPageChange: handlePageChange,
    page: Math.floor(offset / limit) + 1,
    pageCount: pageCount
  }) : null;
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": "restore-reports-table",
    children: tableDataStatus === UNINITIALIZED || tableDataStatus === PENDING ? PageLoading : tableData.isEmpty() ? EmptyState : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(Table, {
        condensed: true,
        tableLayout: "fixed",
        children: [TableColGroup, /*#__PURE__*/_jsx(RestoreReportsTableHeader, {
          actionableRows: actionableRows,
          tableData: tableData
        }), TableBody]
      }), Paginator]
    })
  });
};
RestoreReportsTable.propTypes = {
  actions: PropTypes.object.isRequired,
  buOptions: PropTypes.array,
  isBusinessUnitsLoading: PropTypes.bool,
  reportCount: PropTypes.number,
  filters: PropTypes.object.isRequired,
  tableData: PropTypes.instanceOf(List).isRequired,
  tableDataStatus: PropTypes.oneOf([UNINITIALIZED, PENDING, SUCCEEDED, FAILED]).isRequired
};
const mapStateToProps = state => {
  const {
    auth,
    reports
  } = state;
  const tableDataStatus = selectRestorableReportsRequestStatus(state);
  const tableData = selectRestoreTableData(state);
  return {
    filters: reports.get('filters').toJS(),
    reportCount: reports.getIn(['restorableReports', 'total']),
    tableData,
    tableDataStatus,
    userRemoteId: getIn(['user', 'user_id'])(auth)
  };
};
const mapDispatchToProps = dispatch => ({
  actions: Object.assign({}, bindActionCreators({
    setRestorableReportsFilters
  }, dispatch))
});
export default connect(mapStateToProps, mapDispatchToProps)(RestoreReportsTable);